import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import moment from 'moment/moment';

import { getProductExpirationPromotionsState } from '../../../../../features/price/get_product_expiration_promotions';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../core/constants';
import { DeleteButton } from '../../../../common/button';
import { deletePromotionNotificationData } from '../../../../../core/utils/notification_utils';
import useNotification from '../../../../../core/hooks/use_notification';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { getAllPodsState } from '../../../../../features/pod/get_all_pods';
import { deleteProductExpirationPromotionAction } from '../../../../../features/price/delete_product_expiration_promotion';

const ExpirationPromotionsList = ({ refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getProductExpirationPromotions = useSelector(getProductExpirationPromotionsState);
    const getAllPods = useSelector(getAllPodsState);

    const [deletingIds, setDeletingIds] = useState({});

    const onDeleteExpirationPromotion = (id) => {
        setDeletingIds(prev => ({ ...prev, [id]: true }));

        dispatch(deleteProductExpirationPromotionAction(id))
            .then(response => {
                showNotification(deletePromotionNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setDeletingIds(prev => ({ ...prev, [id]: false }));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Partia</TableCell>
                        <TableCell align="center">Punkt sprzedaży</TableCell>
                        <TableCell align="center">Data przydatności</TableCell>
                        <TableCell align="center">Ilość</TableCell>
                        <TableCell align="center">Cena bazowa</TableCell>
                        <TableCell align="center">Aktualna cena produktu</TableCell>
                        <TableCell align="center">Cena promocyjna końcowa</TableCell>
                        <TableCell align="center">Upust [%]</TableCell>
                        <TableCell align="center">Okres obowiązywania</TableCell>
                        <TableCell align="right" sx={ { pr: 3 } }>Akcje</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getProductExpirationPromotions?.data?.content?.map((productExpirationPromotion, index) => {
                            const isDeleting = deletingIds[productExpirationPromotion?.expirationPromotionId] || false;
                            const podName = getAllPods?.data?.find(pod => pod?.id === productExpirationPromotion?.podId);
                            const podLabel = podName ? `${ podName?.indoorPartner?.name } (${ podName?.description }) - ${ podName?.device?.serviceId || 'brak urządzenia' }` : 'Brak danych';

                            return (
                                <TableRow key={ index } hover>
                                    <TableCell>
                                        <Typography variant="body2"
                                                    sx={ { fontWeight: 600 } }>{ productExpirationPromotion?.productBatchId }</Typography>
                                        <Typography variant="caption" color="text.secondary">Nr
                                            mag: { productExpirationPromotion?.warehouseBatchNumber }</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        { podLabel }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" color="error.main" sx={ { fontWeight: 500 } }>
                                            { moment(productExpirationPromotion?.expirationDate + 'T23:59:59', MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">{ productExpirationPromotion?.quantity } szt.</TableCell>
                                    <TableCell
                                        align="center">{ productExpirationPromotion?.basicGrossPrice?.toFixed(2) } zł</TableCell>
                                    <TableCell
                                        align="center">{ productExpirationPromotion?.currentProductGrossPrice?.toFixed(2) } zł</TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" sx={ { fontWeight: 600, color: 'success.main' } }>
                                            { productExpirationPromotion?.promotionGrossPrice?.toFixed(2) } zł
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">{ productExpirationPromotion?.discount } %</TableCell>
                                    <TableCell align="center">
                                        <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                                            <Typography variant="body2">
                                                Od:&nbsp;{ moment(productExpirationPromotion?.timeFrom, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Do:&nbsp;{ moment(productExpirationPromotion?.timeTo, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={ { pr: 2 } }>
                                        <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                               alignItems="center">
                                            <DeleteButton
                                                size="small"
                                                onDeleteClick={ () => onDeleteExpirationPromotion(productExpirationPromotion?.expirationPromotionId) }
                                                isLoading={ isDeleting }
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ExpirationPromotionsList;